import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sendVerifyEmail } from 'api/verification';
import * as RequiredItem from 'components/required-checklist/required-items/RequiredItem';
import * as UpdateEmailFormDialog from 'components/required-checklist/required-items/UpdateEmailFormDialog';
import { useChecklistData } from 'components/required-checklist/useChecklistData';
import { posthogEvents } from 'constants/posthog-events';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { usePosthog } from 'customHooks/usePosthog';
import { GivebutterAxiosError } from 'types/error';

const RequiredVerifyEmail = () => {
    const toast = useToast();
    const { pushEvent } = usePosthog();
    const { id: userId } = useCurrentUser();
    const { accountId } = useCurrentAccount();
    const { checklist } = useChecklistData();
    const { verifyEmail } = checklist;

    const { mutate: sendVerificationEmail, isSuccess: verificationWasResent } = useMutation({
        mutationFn: () => sendVerifyEmail(userId, accountId),
        onSuccess: () => {
            toast.success('Verification email resent!');
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data?.message ?? 'Something went wrong. Please try again.');
        },
    });

    return (
        <RequiredItem.Root>
            <RequiredItem.TextContent className="gap-6 sm:gap-0">
                <div className="flex w-full flex-col items-start gap-3">
                    <RequiredItem.RequiredItemDuration>2 min</RequiredItem.RequiredItemDuration>
                    <div>
                        <RequiredItem.Title>Verify email</RequiredItem.Title>
                        <RequiredItem.Description>
                            Secure your account and unlock all Givebutter fundraising features.
                            We've already sent you a confirmation link—just click to confirm!
                        </RequiredItem.Description>
                    </div>
                </div>
                {verifyEmail ? (
                    <RequiredItem.CompletedIndicator />
                ) : (
                    <div className="flex gap-2">
                        <RequiredItem.PrimaryButton
                            onClick={() => {
                                sendVerificationEmail();
                                pushEvent(posthogEvents.DASHBOARD_CHECKLIST_ITEM_SELECTED, {
                                    checklistItem: 'verifyEmail',
                                });
                            }}
                            disabled={verificationWasResent}
                        >
                            {verificationWasResent ? (
                                <span className="flex items-center gap-1">
                                    <FontAwesomeIcon icon={faCheck} /> Resent
                                </span>
                            ) : (
                                'Resend email'
                            )}
                        </RequiredItem.PrimaryButton>
                        <UpdateEmailFormDialog.Root>
                            <UpdateEmailFormDialog.Trigger>
                                <RequiredItem.SecondaryButton>
                                    Update email
                                </RequiredItem.SecondaryButton>
                            </UpdateEmailFormDialog.Trigger>
                        </UpdateEmailFormDialog.Root>
                    </div>
                )}
            </RequiredItem.TextContent>
            <RequiredItem.Image
                desktopSrc="/images/graphics/homepage-checklist/verifyEmailDesktop.png"
                mobileSrc="/images/graphics/homepage-checklist/verifyEmailMobile.png"
            />
        </RequiredItem.Root>
    );
};

export { RequiredVerifyEmail };
